// =====================================================
// Chat Header
// =====================================================

import React from 'react';
import { useChatStore } from '../store/chatStore';
import { sendMessage } from '../services/api';

const ChatHeader: React.FC = () => {
  const { setIsOpen, language, setLanguage, clearMessages, setSessionId, addMessage, setIsLoading, userId } = useChatStore();

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleLanguageToggle = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  const handleStartFresh = async () => {
    if (window.confirm(language === 'en'
      ? 'Start a new conversation? This will clear your chat history.'
      : '¿Comenzar una nueva conversación? Esto borrará tu historial de chat.')) {
      // Clear messages and session
      clearMessages();

      // Generate new session ID
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      setSessionId(newSessionId);

      // Start new conversation by sending initial message
      setIsLoading(true);
      try {
        const response = await sendMessage({
          session_id: newSessionId,
          user_id: userId,
          message: 'main_menu',
          language,
          current_url: window.location.href
        });

        // Add bot's welcome message
        addMessage({
          id: Date.now().toString(),
          sender: 'bot',
          text: response.message,
          timestamp: new Date(),
          buttons: response.buttons,
          card: response.card,
          cards: response.cards,
          dropdown: response.dropdown,
          showAppointmentForm: response.showAppointmentForm
        });
      } catch (error) {
        console.error('Failed to start new conversation:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="bg-betenbough-gunmetal text-white px-3 sm:px-4 py-3 flex items-center justify-between
                    pt-[max(0.75rem,env(safe-area-inset-top))]">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
          {/* Replace /ava-logo.png with your actual logo file path */}
          <img
            src="/ava-logo.png"
            alt="Ava Logo"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to letter if image doesn't load
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.innerHTML = '<span class="text-betenbough-gunmetal font-bold text-lg">A</span>';
            }}
          />
        </div>
        <div>
          <h3 className="font-neutraface font-bold text-lg">Ava</h3>
          <p className="text-xs opacity-90 font-filson">Betenbough Homes</p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {/* Language Toggle Switch */}
        <div
          onClick={handleLanguageToggle}
          className="relative flex items-center w-16 h-7 bg-white/20 rounded-full p-0.5 cursor-pointer"
          title={language === 'en' ? 'Switch to Spanish' : 'Cambiar a inglés'}
        >
          {/* Sliding pill indicator */}
          <div
            className={`absolute w-[calc(50%-2px)] h-[calc(100%-4px)] bg-white rounded-full shadow-sm transition-transform duration-200 ${
              language === 'es' ? 'translate-x-[calc(100%+2px)]' : 'translate-x-0'
            }`}
          />
          {/* Labels */}
          <span className={`relative z-10 flex-1 text-center text-[10px] font-semibold transition-colors duration-200 ${
            language === 'en' ? 'text-betenbough-gunmetal' : 'text-white'
          }`}>
            EN
          </span>
          <span className={`relative z-10 flex-1 text-center text-[10px] font-semibold transition-colors duration-200 ${
            language === 'es' ? 'text-betenbough-gunmetal' : 'text-white'
          }`}>
            ES
          </span>
        </div>

        {/* Start Fresh */}
        <button
          onClick={handleStartFresh}
          className="text-white hover:bg-white hover:bg-opacity-20 
                     p-1 rounded transition-colors"
          title={language === 'en' ? 'Start fresh' : 'Empezar de nuevo'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        {/* Close */}
        <button
          onClick={handleClose}
          className="text-white hover:bg-white hover:bg-opacity-20
                     p-1 rounded transition-colors"
          aria-label="Close chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;